/**
 * Cookie/storage consent state.
 *
 * Vixo currently sets only strictly necessary storage: the Firebase Auth
 * session (browserLocalPersistence) and a couple of localStorage keys for
 * impersonation and UI preferences. Under GDPR/ePrivacy and the UK PECR,
 * strictly necessary storage does not require consent, so there is nothing to
 * gate today and ANALYTICS_AVAILABLE is false.
 *
 * The machinery exists so that the moment optional tracking is switched on,
 * consent is enforced rather than retrofitted. See ANALYTICS_AVAILABLE below
 * for exactly what to change.
 */

const STORAGE_KEY = 'vixo_cookie_consent';

/**
 * Bump when the categories below change materially. A stored decision from an
 * older version is treated as absent, so users are asked again rather than
 * silently carried over to a policy they never saw.
 */
const CONSENT_VERSION = 1;

export const CONSENT_CHANGED_EVENT = 'vixo-cookie-consent-changed';

/**
 * Whether the app loads any optional, consent-requiring tracking.
 *
 * FALSE today: firebase.js carries a `measurementId` in its config, but it
 * never imports firebase/analytics or calls getAnalytics(), so no Google
 * Analytics cookie is ever set. The id alone is inert.
 *
 * To enable analytics honestly:
 *   1. Flip this to true.
 *   2. Gate the loader on consent, e.g. in firebase.js:
 *        if (hasConsent('analytics')) { getAnalytics(app); }
 *      and subscribe to CONSENT_CHANGED_EVENT so a later opt-in takes effect
 *      without a reload.
 *   3. Update the cookie section of the Privacy Policy to name the cookies.
 *
 * Flipping this turns the notice banner into a real accept/reject choice.
 */
export const ANALYTICS_AVAILABLE = false;

const defaultConsent = () => ({
    version: CONSENT_VERSION,
    // Essential storage is not optional; recorded for completeness so the
    // stored object describes the full picture rather than implying choice.
    essential: true,
    analytics: false,
    decidedAt: null
});

const readStored = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== CONSENT_VERSION) return null;

        return {
            ...defaultConsent(),
            analytics: Boolean(parsed.analytics),
            decidedAt: parsed.decidedAt || null
        };
    } catch (err) {
        // Private browsing, disabled storage, or corrupt JSON. Treat as
        // undecided rather than throwing during render.
        return null;
    }
};

/** Current consent, falling back to all-optional-off when undecided. */
export function getConsent() {
    return readStored() || defaultConsent();
}

/** True when the banner should be shown, i.e. no valid decision is stored. */
export function needsDecision() {
    return readStored() === null;
}

/**
 * True when the user has actively permitted a category. Essential storage is
 * always permitted; optional categories default to false until opted in, so a
 * storage failure can never be read as consent.
 */
export function hasConsent(category) {
    if (category === 'essential') return true;
    return Boolean(getConsent()[category]);
}

/** Records a decision and notifies listeners in this tab. */
export function setConsent({ analytics }) {
    const next = {
        ...defaultConsent(),
        analytics: Boolean(analytics),
        decidedAt: new Date().toISOString()
    };

    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (err) {
        // Nothing persisted, so the banner reappears next visit. The in-memory
        // event still fires so the current page reflects the choice.
        console.warn('Could not persist cookie consent:', err);
    }

    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: next }));
    return next;
}

/**
 * Clears the stored decision so the banner reappears. Backs the "Cookie
 * preferences" footer link: withdrawing consent must be as easy as giving it.
 */
export function resetConsent() {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
        console.warn('Could not clear cookie consent:', err);
    }

    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: null }));
}

/** Subscribes to consent changes in this tab and other tabs. Returns cleanup. */
export function subscribeToConsent(callback) {
    const onChange = () => callback(getConsent());
    const onStorage = (event) => {
        if (event.key === STORAGE_KEY) callback(getConsent());
    };

    window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
    window.addEventListener('storage', onStorage);

    return () => {
        window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
        window.removeEventListener('storage', onStorage);
    };
}
